import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { displaySpecLabel, localizedMarketText, localizedRecordLabel } from '@/i18n/catalogDisplayText';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import HoverCopyField from '@/temu-platform/modules/products/components/HoverCopyField';
import TemuProductThumb from '@/temu-platform/modules/products/components/TemuProductThumb';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import { downloadCsv } from '@/temu-platform/modules/products/components/TemuPageToolbar';
import TemuOpsListToolbar from '@/temu-platform/components/TemuOpsListToolbar';
import BatchQueryModal from '@/temu-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/temu-platform/modules/products/components/batchQueryUtils';
import { openTemuPathInNewTab } from '@/temu-platform/constants/temuRoutes';
import { useTemuPageShopScope, stackShopDimensionTabs } from '@/temu-platform/hooks/useTemuPageShopScope';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import { checkTemuProductEditable } from '@/temu-platform/utils/checkTemuProductEditable';
import { fetchAllPages } from '@/temu-platform/utils/temuExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import {
  PUBLISH_RECORD_STATUS_TABS,
  resolvePublishStatusTone,
} from '@/temu-platform/utils/temuPublishRecordStatus';
import { temuPlatformAPI } from '@/utils/api';
import { formatPriceRangeDisplay } from '@/utils/formatPriceRange';
import { showToast } from '@/utils/toast';
import {
  getTemuPublishLifecycleSyncedAt,
  resolveTemuSiteDisplayName,
} from '@/temu-platform/utils/temuLocalPublishRecordStore';
import { formatShanghaiDateTime } from '@/temu-platform/utils/formatBeijingDateTime';

const PUBLISH_RECORD_EXPORT_HEADERS = [
  'id', 'title', 'supplierCode', 'platformSpuName', 'status',
  'publishSites', 'skus', 'supplyPrice', 'action', 'source',
  'createdAt', 'publishedAt', 'finishedAt', 'listedAt', 'hopeOnSaleDate',
];

/** 发布记录 SKU 列默认只平铺 1 条，其余走「查看更多」 */
const SKU_PREVIEW_LIMIT = 1;
/** 发布站点列默认平铺条数 */
const SITE_PREVIEW_LIMIT = 2;

const ID_TYPE_OPTIONS = [
  { value: 'skc', label: 'SKC' },
  { value: 'spu', label: 'SPU' },
  { value: 'sku', label: 'SKU' },
];

const EMPTY_FILTERS = {
  idType: 'skc',
  idValue: '',
  title: '',
  supplierCode: '',
};

const EMPTY_QUERY = {
  status: '',
  idType: 'skc',
  keywords: '',
  title: '',
  supplierCode: '',
};

const BATCH_FIELDS = {
  idValue: {
    titleKey: 'idType',
    titleMap: { skc: 'publishRecord.batchSKC', spu: 'publishRecord.batchSPU', sku: 'publishRecord.batchSKU' },
    placeholder: 'publishRecord.batchCodePh',
  },
  title: {
    title: 'publishRecord.batchTitle',
    placeholder: 'publishRecord.batchTitlePh',
  },
  supplierCode: {
    title: 'publishRecord.batchSupplier',
    placeholder: 'publishRecord.batchSupplierPh',
  },
};

const STATUS_TABS = PUBLISH_RECORD_STATUS_TABS;

function statusTabCount(stats, tab) {
  if (!stats) return 0;
  if (tab.id === 'all') return Number(stats.all) || 0;
  return Number(stats[tab.status]) || 0;
}

function resolveStatusLabel(item) {
  return String(item?.statusLabel ?? item?.status ?? '待审核').trim() || '待审核';
}

function resolveRejectRemark(skc, record) {
  const skcRemark = String(skc?.remark || skc?.rejectRemark || '').trim();
  if (skcRemark) return skcRemark;
  return String(record?.remark || record?.rejectRemark || '').trim();
}

function resolveSkcSitePriceLines(skc, record) {
  if (Array.isArray(skc?.sitePriceLines) && skc.sitePriceLines.length) {
    return skc.sitePriceLines;
  }
  const siteText = (Array.isArray(record?.siteLabels) && record.siteLabels.length)
    ? record.siteLabels.join('、')
    : (Array.isArray(skc?.siteLabels) && skc.siteLabels.length
      ? skc.siteLabels.join('、')
      : '');
  if (!siteText) return [];
  return [{ skuLabel: '', siteText, priceText: '—' }];
}

/** 站点列：去重后的站点中文名 */
function resolveUniqueSiteTexts(lines) {
  const seen = new Set();
  const sites = [];
  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const text = resolveTemuSiteDisplayName(line?.siteText) || String(line?.siteText || '').trim();
    if (!text || text === '—' || seen.has(text)) return;
    seen.add(text);
    sites.push(text);
  });
  return sites;
}

/**
 * SKU 列条目：按规格标签去重；同规格多站点时价差进 priceTexts。
 * 展示文案为「尺码：平台SKU」，如 110：469362597171。
 */
function resolveSkcSkuEntries(skc, lines, recordProductSkuIds = []) {
  const productSkuIds = [
    ...(Array.isArray(skc?.productSkuIds) ? skc.productSkuIds : []),
    ...(Array.isArray(recordProductSkuIds) ? recordProductSkuIds : []),
  ]
    .map((id) => String(id ?? '').trim())
    .filter(Boolean)
    .filter((id, idx, arr) => arr.indexOf(id) === idx);
  const byLabel = new Map();
  let lineSkuIndex = 0;
  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const label = String(line?.skuLabel || '').trim() || 'SKU';
    let entry = byLabel.get(label);
    if (!entry) {
      const fromLine = String(line?.platformSkuId || line?.productSkuId || '').trim();
      entry = {
        key: label,
        skuLabel: label === 'SKU' ? '' : label,
        platformSkuId: fromLine || productSkuIds[lineSkuIndex] || '',
        siteTexts: [],
        priceTexts: [],
      };
      byLabel.set(label, entry);
      lineSkuIndex += 1;
    } else if (!entry.platformSkuId) {
      entry.platformSkuId = String(line?.platformSkuId || line?.productSkuId || '').trim();
    }
    const site = resolveTemuSiteDisplayName(line?.siteText) || String(line?.siteText || '').trim();
    if (site && site !== '—' && !entry.siteTexts.includes(site)) {
      entry.siteTexts.push(site);
    }
    const price = String(line?.priceText || '').trim();
    if (price && price !== '—') entry.priceTexts.push(price);
  });
  let entries = [...byLabel.values()];
  if (!entries.length && productSkuIds.length) {
    entries = productSkuIds.map((id) => ({
      key: id,
      skuLabel: '',
      platformSkuId: id,
      siteTexts: [],
      priceTexts: [],
    }));
  } else if (productSkuIds.length) {
    entries = entries.map((entry, idx) => ({
      ...entry,
      platformSkuId: entry.platformSkuId || productSkuIds[idx] || '',
    }));
  }
  return entries;
}

/** 尺码：平台SKU，例如 110：469362597171 */
function formatSkuEntryDisplay(entry) {
  const size = String(entry?.skuLabel || '').trim();
  const id = String(entry?.platformSkuId || '').trim();
  if (size && id) return `${size}：${id}`;
  return size || id || '—';
}

function resolveSkcSupplyPriceText(lines) {
  return formatPriceRangeDisplay(
    (Array.isArray(lines) ? lines : []).map((line) => ({
      priceText: line?.priceText,
    })),
  );
}

/** 发布记录「查看更多」：SKU / 站点 / 供货价完整明细 */
function PublishRecordSkuModal({ open, skc = null, lines = [], recordProductSkuIds = [], onClose }) {
  const { t, i18n } = useTranslation('workbench');
  if (!open || !skc) return null;
  const skuEntries = resolveSkcSkuEntries(skc, lines, recordProductSkuIds);
  const rows = skuEntries.length
    ? skuEntries.map((entry) => ({
      skuText: formatSkuEntryDisplay(entry),
      siteText: localizedMarketText(entry.siteTexts.join('、'), i18n.language) || '—',
      priceText: resolveSkcSupplyPriceText(
        (entry.priceTexts || []).map((priceText) => ({ priceText })),
      ),
    }))
    : (Array.isArray(lines) ? lines : []).map((line) => ({
      skuText: formatSkuEntryDisplay({
        skuLabel: line.skuLabel,
        platformSkuId: line.platformSkuId || line.productSkuId,
      }),
      siteText: localizedMarketText(line.siteText, i18n.language) || '—',
      priceText: line.priceText || '—',
    }));
  const stop = (event) => event.stopPropagation();
  return createPortal(
    <div
      className="tpo-sku-modal"
      role="presentation"
      onClick={onClose}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose?.();
      }}
    >
      <div className="tpo-sku-modal__backdrop" aria-hidden="true" />
      <div
        className="tpo-sku-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-publish-sku-modal-title"
        onClick={stop}
      >
        <div className="tpo-sku-modal__head">
          <div className="tpo-sku-modal__head-main">
            <h2 id="tpo-publish-sku-modal-title">{t('publishRecord.skuModalTitle')}</h2>
            <p className="tpo-sku-modal__meta">
              {skc.colorLabel ? (
                <>
                  <span className="tpo-sku-modal__meta-text">{displaySpecLabel(skc.colorLabel)}</span>
                  <span className="tpo-sku-modal__meta-sep" aria-hidden="true">·</span>
                </>
              ) : null}
              <HoverCopyField
                label="SKC"
                value={skc.skcName || ''}
                separator="："
                className="tpo-sku-modal__meta-copy"
              />
              <span className="tpo-sku-modal__meta-text">{t('publishRecord.rowCount', { count: rows.length })}</span>
            </p>
          </div>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose}>
            {t('publishRecord.close')}
          </button>
        </div>
        <div className="tpo-sku-modal__body">
          {rows.length === 0 ? (
            <div className="tpo-sku-modal__empty">{t('publishRecord.noDetail')}</div>
          ) : (
            <table className="tpo-sku-modal__table">
              <thead>
                <tr>
                  <th scope="col">SKU</th>
                  <th scope="col">{t('publishRecord.publishSite')}</th>
                  <th scope="col">{t('publishRecord.supplyPrice')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={`${row.skuText}-${idx}`}>
                    <td>
                      <HoverCopyField value={row.skuText || '—'} />
                    </td>
                    <td>{row.siteText || '—'}</td>
                    <td>{row.priceText || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="tpo-sku-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={onClose}>
            {t('publishRecord.gotIt')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function resolveSkcStatusLabel(skc, record) {
  const label = String(skc?.statusLabel ?? skc?.status ?? '').trim();
  if (label) return label;
  return resolveStatusLabel(record);
}

function resolveSkcLifecycleHint(skc) {
  const select = String(skc?.selectStatusLabel || '').trim();
  const jit = String(skc?.applyJitStatusLabel || '').trim();
  if (select && jit) return `${select} · ${jit}`;
  return select || jit || '';
}

function canDeleteRecord(item) {
  if (item?.canDelete === false) return false;
  return Boolean(item?.id);
}

function RecordSkcThumb({ imageUrl }) {
  return <TemuProductThumb imageUrl={imageUrl} />;
}

/** 发布记录：布局对齐草稿箱，并增加状态列 */
export default function ProductPublishRecordsPage() {
  const { t, i18n } = useTranslation('workbench');
  const { shopDimensionTabs, concreteScopeId, allShopsScope } = useTemuPageShopScope();
  const shopId = concreteScopeId;
  const location = useLocation();

  const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
  const [query, setQuery] = useState(EMPTY_QUERY);
  const [statusDimension, setStatusDimension] = useState('all');
  const [statusCounts, setStatusCounts] = useState(null);
  const [queryNonce, setQueryNonce] = useState(0);
  const [batchModalField, setBatchModalField] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [toolbarBusy, setToolbarBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [skuDetailModal, setSkuDetailModal] = useState(null);
  const selectedRowsRef = useRef(new Map());

  useEffect(() => {
    if (!shopId) {
      setLastSyncedAt('');
      return;
    }
    setLastSyncedAt(getTemuPublishLifecycleSyncedAt(shopId) || '');
  }, [shopId]);

  useEffect(() => {
    const map = selectedRowsRef.current;
    for (const id of [...map.keys()]) {
      if (!selectedIds.has(id)) map.delete(id);
    }
    items.forEach((row) => {
      if (row?.id != null && selectedIds.has(row.id)) map.set(row.id, row);
    });
  }, [items, selectedIds]);

  // 发品成功跳转进来：强制重新拉列表
  useEffect(() => {
    if (!location.state?.refreshPublishRecords) return undefined;
    setPage(1);
    setQueryNonce((n) => n + 1);
    const t = window.setTimeout(() => setQueryNonce((n) => n + 1), 800);
    return () => window.clearTimeout(t);
  }, [location.state?.refreshPublishRecords, location.state?.at]);

  const loadRecords = useCallback(async () => {
    if (!shopId) {
      setItems([]);
      setTotal(0);
      setStatusCounts(null);
      setSelectedIds(new Set());
      return;
    }
    setLoading(true);
    try {
      const res = await temuPlatformAPI.listPublishRecords(shopId, {
        status: query.status || undefined,
        idType: query.keywords ? (query.idType || 'skc') : undefined,
        keywords: query.keywords || undefined,
        title: query.title || undefined,
        supplierCode: query.supplierCode || undefined,
        page,
        pageSize,
      });
      const nextItems = Array.isArray(res?.items) ? res.items : [];
      setItems(nextItems);
      setTotal(Number(res?.total) || 0);
      const counts = res?.statusCounts && typeof res.statusCounts === 'object' ? res.statusCounts : null;
      setStatusCounts(counts);
    } catch (e) {
      setItems([]);
      setTotal(0);
      setStatusCounts(null);
      showToast(e?.message || t('publishRecord.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, query.status, query.idType, query.keywords, query.title, query.supplierCode, page, pageSize, t]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords, queryNonce]);

  useEffect(() => {
    setFilterDraft(EMPTY_FILTERS);
    setQuery(EMPTY_QUERY);
    setBatchModalField(null);
    setPage(1);
    setQueryNonce((n) => n + 1);
    setSelectedIds(new Set());
  }, [shopId]);

  const selectableIds = useMemo(() => items.map((row) => row.id).filter(Boolean), [items]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const toggleAll = () => {
    setSelectedIds((prev) => togglePageSelection(prev, selectableIds, allSelected));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const openBatchModal = (fieldKey) => {
    setBatchModalField(fieldKey);
  };

  const onBatchConfirm = (tokens) => {
    if (!batchModalField) return;
    updateField(batchModalField, joinBatchTokens(tokens));
  };

  const batchModalMeta = batchModalField ? BATCH_FIELDS[batchModalField] : null;
  const batchModalTitle = t(batchModalMeta
    ? (batchModalMeta.title
      || batchModalMeta.titleMap?.[filterDraft[batchModalMeta.titleKey]]
      || 'publishRecord.batchQuery')
    : 'publishRecord.batchQuery');
  const idTypeLabel = ID_TYPE_OPTIONS.find((o) => o.value === filterDraft.idType)?.label || 'SKC';

  const onQuery = () => {
    let idTokens = parseBatchTokens(filterDraft.idValue).slice(0, BATCH_QUERY_LIMIT);
    let titleTokens = parseBatchTokens(filterDraft.title).slice(0, 5);
    let supplierTokens = parseBatchTokens(filterDraft.supplierCode).slice(0, 5);
    const idType = filterDraft.idType || 'skc';
    if (idType !== 'spu' && idTokens.length > 20) {
      idTokens = idTokens.slice(0, 20);
      showToast(t('publishRecord.batchIdCap'), 'info');
    } else if (parseBatchTokens(filterDraft.idValue).length > BATCH_QUERY_LIMIT) {
      showToast(t('publishRecord.batchCodeCap', { limit: BATCH_QUERY_LIMIT }), 'info');
    }
    if (parseBatchTokens(filterDraft.title).length > 5) {
      showToast(t('publishRecord.batchTitleCap'), 'info');
    }
    if (parseBatchTokens(filterDraft.supplierCode).length > 5) {
      showToast(t('publishRecord.batchSupplierCap'), 'info');
    }
    if (shopId) setLoading(true);
    setPage(1);
    setQuery({
      status: statusDimension === 'all' ? '' : statusDimension,
      idType,
      keywords: idTokens.join(','),
      title: titleTokens.join(','),
      supplierCode: supplierTokens.join(','),
    });
    setQueryNonce((n) => n + 1);
  };

  const onStatusDimensionChange = (nextId) => {
    if (!nextId || nextId === statusDimension) return;
    setStatusDimension(nextId);
    setPage(1);
    setSelectedIds(new Set());
    if (shopId) setLoading(true);
    setQuery((prev) => ({
      ...prev,
      status: nextId === 'all' ? '' : nextId,
    }));
    setQueryNonce((n) => n + 1);
  };

  const statusTabsInner = useMemo(() => (
    <div className="tpo-status-tabs" role="tablist" aria-label={t('publishRecord.tabsAria')}>
      {STATUS_TABS.map((tab) => {
        const active = statusDimension === tab.id;
        const count = statusTabCount(statusCounts, tab);
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            className={`tpo-status-tab${active ? ' tpo-status-tab--active' : ''}`}
            onClick={() => onStatusDimensionChange(tab.id)}
          >
            {t(`publishRecord.${({
              all: 'tabAll',
              '待审核': 'tabPendingReview',
              '已通过': 'tabApproved',
              '已上架': 'tabListed',
              '已驳回': 'tabRejected',
              '待商家确认': 'tabMerchant',
              '处理中': 'tabProcessing',
              '接受失败': 'tabAcceptFailed',
              '已撤回': 'tabWithdrawn',
              '申诉中': 'tabAppeal',
            })[tab.id] || 'tabAll'}`, { defaultValue: tab.label })}
            <span className="tpo-status-tab__count">{count}</span>
          </button>
        );
      })}
    </div>
  ), [statusDimension, statusCounts, t]);

  const statusTabs = useMemo(
    () => stackShopDimensionTabs(shopDimensionTabs, statusTabsInner),
    [shopDimensionTabs, statusTabsInner],
  );

  const onReset = () => {
    setFilterDraft(EMPTY_FILTERS);
    setStatusDimension('all');
    setQuery(EMPTY_QUERY);
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const onCopyToDraft = async (item) => {
    if (!shopId) {
      showToast(t('publishRecord.pickShop'), 'warning');
      return;
    }
    if (!item?.id) {
      showToast(t('publishRecord.missingRecord'), 'warning');
      return;
    }
    if (copyingId) return;
    setCopyingId(item.id);
    try {
      const res = await temuPlatformAPI.copyPublishRecordToDraft(shopId, item.id, {
        spuName: item.platformSpuName || '',
      });
      const draftId = res?.id;
      showToast(t('publishRecord.copied'), 'success');
      if (draftId) {
        openTemuPathInNewTab(`products/publish?draftId=${encodeURIComponent(draftId)}`);
      } else {
        openTemuPathInNewTab('products/drafts');
      }
    } catch (e) {
      showToast(e?.message || t('publishRecord.copyFailed'), 'error');
    } finally {
      setCopyingId(null);
    }
  };

  const onEdit = async (item) => {
    // 有平台 SPU：先校验是否可编辑，再打开编辑页
    if (item?.platformSpuName) {
      if (!shopId) {
        showToast(t('publishRecord.pickShop'), 'warning');
        return;
      }
      setEditingId(item.id);
      try {
        const check = await checkTemuProductEditable(shopId, item.platformSpuName);
        if (!check.ok) {
          showToast(check.reason || t('publishRecord.notEditable'), 'warning');
          return;
        }
        const qs = new URLSearchParams();
        if (item.id != null) qs.set('recordId', String(item.id));
        if (item.draftId != null) qs.set('draftId', String(item.draftId));
        const q = qs.toString();
        openTemuPathInNewTab(
          `products/edit/${encodeURIComponent(item.platformSpuName)}${q ? `?${q}` : ''}`,
        );
      } finally {
        setEditingId(null);
      }
      return;
    }
    // 无 SPU（常见于历史「假成功」记录）：回填关联草稿表单；发品后草稿会软删，后端仍允许按 ID 读取
    if (item?.draftId) {
      openTemuPathInNewTab(`products/publish?draftId=${encodeURIComponent(item.draftId)}`);
      return;
    }
    showToast(t('publishRecord.cannotEdit'), 'warning');
  };

  const writePublishRecordsCsv = useCallback((rows, filename) => {
    downloadCsv(
      filename,
      PUBLISH_RECORD_EXPORT_HEADERS,
      rows.map((row) => {
        const allLines = Array.isArray(row?.skcItems)
          ? (row.skcItems || []).flatMap((skc) => resolveSkcSitePriceLines(skc, row))
          : [];
        return {
          id: row.id,
          title: row.title,
          supplierCode: row.supplierCode,
          platformSpuName: row.platformSpuName,
          status: resolveStatusLabel(row),
          publishSites: resolveUniqueSiteTexts(allLines).join('；'),
          skus: [...new Set(
            allLines.map((line) => String(line.skuLabel || '').trim()).filter(Boolean),
          )].join('；'),
          supplyPrice: resolveSkcSupplyPriceText(allLines),
          action: row.action,
          source: row.source,
          createdAt: row.createdAt,
          publishedAt: row.publishedAt || row.finishedAt,
          finishedAt: row.finishedAt,
          listedAt: row.listedAt || '',
          hopeOnSaleDate: row.hopeOnSaleDate || row.shelfLabel || '',
        };
      }),
    );
  }, []);

  const fetchAllPublishRecords = useCallback(async () => {
    if (!shopId) return [];
    return fetchAllPages(async (pageNo, size) => {
      const res = await temuPlatformAPI.listPublishRecords(shopId, {
        status: query.status || undefined,
        idType: query.keywords ? (query.idType || 'skc') : undefined,
        keywords: query.keywords || undefined,
        title: query.title || undefined,
        supplierCode: query.supplierCode || undefined,
        page: pageNo,
        pageSize: size,
      });
      return {
        items: Array.isArray(res?.items) ? res.items : [],
        total: Number(res?.total) || 0,
      };
    });
  }, [shopId, query.status, query.idType, query.keywords, query.title, query.supplierCode]);

  const exportFilteredRecords = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const rows = await fetchAllPublishRecords();
      if (!rows.length) {
        showToast(t('publishRecord.noneToExport'), 'warning');
        return;
      }
      writePublishRecordsCsv(rows, `temu-publish-records-filtered-${Date.now()}.csv`);
      showToast(t('publishRecord.exported', { count: rows.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('publishRecord.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  }, [shopId, exporting, fetchAllPublishRecords, writePublishRecordsCsv, t]);

  const exportPageRecords = useCallback(() => {
    if (!items.length) {
      showToast(t('publishRecord.noneToExport'), 'warning');
      return;
    }
    writePublishRecordsCsv(items, `temu-publish-records-page-${Date.now()}.csv`);
    showToast(t('publishRecord.exported', { count: items.length }), 'success');
  }, [items, writePublishRecordsCsv, t]);

  const exportSelectedRecords = useCallback(() => {
    const rows = Array.from(selectedIds)
      .map((id) => selectedRowsRef.current.get(id))
      .filter(Boolean);
    if (!rows.length) {
      showToast(t('publishRecord.pickToExport'), 'warning');
      return;
    }
    writePublishRecordsCsv(rows, `temu-publish-records-selected-${Date.now()}.csv`);
    showToast(t('publishRecord.exportedSelected', { count: rows.length }), 'success');
  }, [selectedIds, writePublishRecordsCsv, t]);

  const onBatchDelete = async () => {
    if (!shopId || selectedIds.size === 0) {
      showToast(t('publishRecord.pickToDelete'), 'warning');
      return;
    }
    // TEMU 发布记录存在浏览器本地（id 形如 tpr-xxx），不能用 Number 过滤，
    // 否则整批 id 会被丢掉，删除永远不生效。
    const ids = Array.from(selectedIds)
      .map((id) => String(id ?? '').trim())
      .filter(Boolean);
    if (!ids.length) {
      showToast(t('publishRecord.invalidSelection'), 'warning');
      return;
    }
    const confirmed = await showTemuConfirm({
      title: t('publishRecord.batchDelete'),
      message: t('publishRecord.batchDeleteMsg', { count: ids.length, platform: 'TEMU' }),
      tone: 'danger',
      confirmText: t('publishRecord.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!confirmed) return;
    setToolbarBusy(true);
    try {
      const res = await temuPlatformAPI.batchDeletePublishRecords(shopId, ids);
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : ids.length;
      if (count > 0) {
        showToast(t('publishRecord.deletedCount', { count }), 'success');
      } else {
        showToast(t('publishRecord.noneDeleted'), 'warning');
      }
      setSelectedIds(new Set());
      loadRecords();
    } catch (e) {
      showToast(e?.message || t('publishRecord.batchDeleteFailed'), 'error');
    } finally {
      setToolbarBusy(false);
    }
  };

  const lastSyncedLabel = useMemo(() => {
    if (!lastSyncedAt) return '';
    return t('publishRecord.lastPull', { time: formatShanghaiDateTime(lastSyncedAt) });
  }, [lastSyncedAt, t]);

  const onSyncLifecycle = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickShop'), 'warning');
      return;
    }
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await temuPlatformAPI.syncPublishRecordLifecycle(shopId);
      setLastSyncedAt(getTemuPublishLifecycleSyncedAt(shopId) || new Date().toISOString());
      if (res?.skipped) {
        const msg = res.reason === 'empty-records'
          ? t('publishRecord.noRecordsLifecycle')
          : t('publishRecord.noSkuLifecycle');
        showToast(msg, 'info');
        setQueryNonce((n) => n + 1);
        return;
      }
      const count = Array.isArray(res?.items) ? res.items.length : 0;
      const queried = Number(res?.queriedSkuCount) || 0;
      const mode = res?.queryMode;
      let tip;
      if (count > 0) {
        tip = mode === 'spu-skc'
          ? t('publishRecord.lifecycleMatched', { count })
          : t('publishRecord.lifecycleBySku', { queried, count });
      } else {
        tip = mode === 'spu-skc'
          ? t('publishRecord.lifecycleNone')
          : t('publishRecord.lifecycleSkuNone', { queried });
      }
      showToast(tip, count > 0 ? 'success' : 'info');
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('publishRecord.lifecycleFailed'), 'error');
    } finally {
      setSyncing(false);
    }
  }, [shopId, syncing, t]);

  const colSpan = 10;

  return (
    <>
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedIds.size}
            busy={toolbarBusy || loading || exporting || syncing}
            syncKinds={['publish-record']}
            quotaKind="publish-record"
            onSync={onSyncLifecycle}
            syncing={syncing}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={lastSyncedLabel}
            syncLabel={t('publishRecord.pullLifecycle')}
            syncHint={t('publishRecord.lifecycleHint')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: items.length,
              selectedCount: selectedIds.size,
              onExportFiltered: exportFilteredRecords,
              onExportPage: exportPageRecords,
              onExportSelected: exportSelectedRecords,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!shopId}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline tpo-field--id-combo tpo-field--batch">
              <select
                className="tpo-select tpo-select--compact"
                value={filterDraft.idType}
                disabled={!shopId}
                onChange={(e) => updateField('idType', e.target.value)}
                aria-label={t('publishRecord.idTypeAria')}
              >
                {ID_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <input
                className="tpo-input"
                value={filterDraft.idValue}
                placeholder={t('publishRecord.idPlaceholder', { type: idTypeLabel })}
                disabled={!shopId}
                onChange={(e) => updateField('idValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={t('publishRecord.batchInputId', { type: idTypeLabel })}
                title={t('publishRecord.batchQuery')}
                disabled={!shopId}
                onClick={() => openBatchModal('idValue')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="tpo-field tpo-field--inline tpo-field--batch">
              <span className="tpo-field__label">{t('publishRecord.title')}</span>
              <input
                className="tpo-input"
                value={filterDraft.title}
                placeholder={t('publishRecord.titlePlaceholder')}
                disabled={!shopId}
                onChange={(e) => updateField('title', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={t('publishRecord.batchInputTitle')}
                title={t('publishRecord.batchQuery')}
                disabled={!shopId}
                onClick={() => openBatchModal('title')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="tpo-field tpo-field--inline tpo-field--batch">
              <span className="tpo-field__label">{t('publishRecord.supplier')}</span>
              <input
                className="tpo-input"
                value={filterDraft.supplierCode}
                placeholder={t('publishRecord.supplierPlaceholder')}
                disabled={!shopId}
                onChange={(e) => updateField('supplierCode', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={t('publishRecord.batchInputSupplier')}
                title={t('publishRecord.batchQuery')}
                disabled={!shopId}
                onClick={() => openBatchModal('supplierCode')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="tpo-filter-bar__actions">
            <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onReset} disabled={!shopId}>
              {t('publishRecord.reset')}
            </button>
            <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={onQuery} disabled={!shopId}>
              {t('publishRecord.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={statusTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('publishRecord.querying')}
      >
        <div className={`wf-tasks-page platform-product-page wf-tasks-page--embedded platform-product-page--ops-chrome${loading ? ' platform-product-page--loading' : ''}`}>
          <div className="wf-tasks-table-panel">
            <div className="wf-tasks-table-wrap" role="region" aria-live="polite">
              <table className="wf-tasks-table platform-product-table platform-product-table--temu platform-product-table--drafts platform-product-table--records">
                <colgroup>
                  <col className="temu-product-col--check" />
                  <col className="temu-product-col--info" />
                  <col className="temu-product-col--skc" />
                  <col className="temu-product-col--type" />
                  <col className="temu-product-col--status" />
                  <col className="temu-product-col--sites" />
                  <col className="temu-product-col--sku" />
                  <col className="temu-product-col--price" />
                  <col className="temu-product-col--time" />
                  <col className="temu-product-col--ops" />
                </colgroup>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="temu-product-cell--spu-check"
                      onClick={() => {
                        if (!shopId || loading || selectableIds.length === 0) return;
                        toggleAll();
                      }}
                    >
                      <input
                        type="checkbox"
                        aria-label={t('publishRecord.selectAll')}
                        disabled={!shopId || loading || selectableIds.length === 0}
                        checked={allSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={toggleAll}
                      />
                    </th>
                    <th scope="col">{t('publishRecord.productInfo')}</th>
                    <th scope="col">SKC</th>
                    <th scope="col">{t('publishRecord.type')}</th>
                    <th scope="col">{t('publishRecord.status')}</th>
                    <th scope="col">{t('publishRecord.publishSite')}</th>
                    <th scope="col">SKU</th>
                    <th scope="col">{t('publishRecord.supplyPrice')}</th>
                    <th scope="col">{t('publishRecord.time')}</th>
                    <th scope="col" className="temu-product-th--actions">{t('publishRecord.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {!shopId || loading || items.length === 0 ? (
                    <tr>
                      <td colSpan={colSpan} className="wf-tasks-table__empty">
                        <div className="funding-empty-state">
                          <p className="funding-empty-state__title">
                            {!shopId ? t('publishRecord.pickShop') : loading ? t('publishRecord.loading') : t('publishRecord.empty')}
                          </p>
                          {!shopId || loading ? null : (
                            <p className="funding-empty-state__description">
                              {t('publishRecord.emptyHint')}
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    items.flatMap((item) => {
                      const skcItems = Array.isArray(item.skcItems) && item.skcItems.length
                        ? item.skcItems
                        : [{ colorLabel: null, skcName: null, imageUrl: null, merchantSkus: [], supplierCode: null }];
                      const typeLabel = item.typeLabel
                        || (item.source === 'sync' ? t('publishRecord.sourceSync') : (item.action === 'edit' ? t('publishRecord.sourceEdit') : t('publishRecord.sourceNew')));
                      const rowSpan = Math.max(1, skcItems.length);
                      const createdAt = item.createdAt;
                      const publishedAt = item.publishedAt || item.finishedAt;
                      const shelfLabel = item.shelfLabel
                        || item.hopeOnSaleDate
                        || (item.shelfWay === '2' ? '—' : t('publishRecord.autoList'));

                      return skcItems.map((skc, skcIdx) => {
                        const isFirst = skcIdx === 0;
                        const remark = resolveRejectRemark(skc, item);
                        const sitePriceLines = resolveSkcSitePriceLines(skc, item);

                        return (
                          <tr
                            key={`${item.id}-skc-${skcIdx}-${skc.skcName || 'empty'}`}
                            className={`temu-product-row temu-draft-row${isFirst ? '' : ' temu-draft-row--skc-child'}`}
                          >
                            {isFirst ? (
                              <>
                                <td
                                  rowSpan={rowSpan}
                                  className="temu-product-cell temu-product-cell--spu-check"
                                  onClick={() => toggleOne(item.id)}
                                >
                                  <input
                                    type="checkbox"
                                    aria-label={t('publishRecord.selectRecord', { title: item.title || item.id })}
                                    checked={selectedIds.has(item.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggleOne(item.id)}
                                  />
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="temu-product-cell temu-product-cell--info"
                                >
                                  <div className="temu-product-info temu-product-info--text-only temu-draft-product-info">
                                    <div className="temu-product-info__text">
                                      <HoverCopyField
                                        value={item.title || t('publishRecord.unnamed')}
                                        className="temu-product-info__title"
                                        popoverCopy
                                      />
                                      <HoverCopyField
                                        label="SPU"
                                        value={item.platformSpuName || ''}
                                        className="temu-product-info__meta"
                                        separator=": "
                                      />
                                      <div className="temu-product-info__meta">
                                        {t('publishRecord.category', { label: item.categoryLabel || '—' })}
                                      </div>
                                      {item.errorMessage ? (
                                        <div className="temu-product-info__meta temu-record-error" title={item.errorMessage}>
                                          {t('publishRecord.note', { message: item.errorMessage })}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                              </>
                            ) : null}

                            <td className="temu-product-cell temu-product-cell--spec">
                              <div className="temu-product-spec temu-draft-skc">
                                <RecordSkcThumb imageUrl={skc.imageUrl} />
                                <div className="temu-product-spec__text">
                                  <div className="temu-product-spec__color">
                                    {displaySpecLabel(skc.colorLabel) || '—'}
                                  </div>
                                  <HoverCopyField
                                    label="SKC"
                                    value={skc.skcName || ''}
                                    className="temu-product-spec__skc"
                                    separator=": "
                                  />
                                  <HoverCopyField
                                    label={t('publishRecord.supplier')}
                                    value={skc.supplierCode || item.supplierCode || ''}
                                    className="temu-product-spec__skc"
                                    separator=": "
                                    emptyText="—"
                                  />
                                </div>
                              </div>
                            </td>

                            {isFirst ? (
                              <td
                                rowSpan={rowSpan}
                                className="temu-product-cell temu-draft-cell--type"
                              >
                                {localizedRecordLabel(t, typeLabel, i18n.language)}
                              </td>
                            ) : null}
                            <td className="temu-product-cell temu-draft-cell--status">
                              <span className={`tpo-records__status tpo-records__status--${resolvePublishStatusTone(resolveSkcStatusLabel(skc, item))}`}>
                                {localizedRecordLabel(t, resolveSkcStatusLabel(skc, item), i18n.language)}
                              </span>
                              {resolveSkcLifecycleHint(skc) ? (
                                <div className="temu-record-times__row platform-product-table__muted" title={resolveSkcLifecycleHint(skc)}>
                                  {resolveSkcLifecycleHint(skc)}
                                </div>
                              ) : null}
                            </td>
                            <td className="temu-product-cell temu-draft-cell--sites">
                              {(() => {
                                const sites = resolveUniqueSiteTexts(sitePriceLines);
                                if (!sites.length) {
                                  return remark ? (
                                    <span className="temu-record-remark" title={remark}>{remark}</span>
                                  ) : '—';
                                }
                                const previewSites = sites.slice(0, SITE_PREVIEW_LIMIT);
                                const hasMoreSites = sites.length > SITE_PREVIEW_LIMIT;
                                return (
                                  <div className="temu-record-sites" title={sites.join('\n')}>
                                    {previewSites.map((site) => (
                                      <div key={site} className="temu-record-sites__item">{localizedMarketText(site, i18n.language)}</div>
                                    ))}
                                    {hasMoreSites ? (
                                      <button
                                        type="button"
                                        className="temu-record-sku-preview__more"
                                        onClick={() => setSkuDetailModal({
                                          skc,
                                          lines: sitePriceLines,
                                          recordProductSkuIds: item.productSkuIds || [],
                                        })}
                                      >
                                        {t('publishRecord.viewMore', { count: sites.length })}
                                      </button>
                                    ) : null}
                                    {remark ? (
                                      <div className="temu-record-remark platform-product-table__muted" title={remark}>
                                        {remark}
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="temu-product-cell temu-draft-cell--sku">
                              {(() => {
                                const skuEntries = resolveSkcSkuEntries(skc, sitePriceLines, item.productSkuIds);
                                if (!skuEntries.length) return '—';
                                const previewSkus = skuEntries.slice(0, SKU_PREVIEW_LIMIT);
                                const hasMoreSkus = skuEntries.length > SKU_PREVIEW_LIMIT;
                                return (
                                  <div className="temu-record-sku-preview">
                                    {previewSkus.map((entry) => {
                                      const text = formatSkuEntryDisplay(entry);
                                      return (
                                        <div key={entry.key} className="temu-record-sku-preview__item">
                                          <HoverCopyField
                                            value={text === '—' ? '' : text}
                                            className="temu-record-sku-preview__code"
                                            emptyText="—"
                                          />
                                        </div>
                                      );
                                    })}
                                    {hasMoreSkus ? (
                                      <button
                                        type="button"
                                        className="temu-record-sku-preview__more"
                                        onClick={() => setSkuDetailModal({
                                          skc,
                                          lines: sitePriceLines,
                                          recordProductSkuIds: item.productSkuIds || [],
                                        })}
                                      >
                                        {t('publishRecord.viewMore', { count: skuEntries.length })}
                                      </button>
                                    ) : null}
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="temu-product-cell temu-draft-cell--price">
                              <div className="temu-record-price">
                                {resolveSkcSupplyPriceText(sitePriceLines)}
                              </div>
                            </td>
                            {isFirst ? (
                              <>
                                <td
                                  rowSpan={rowSpan}
                                  className="temu-product-cell platform-product-table__muted temu-draft-cell--time"
                                >
                                  <div className="temu-record-times">
                                    <div className="temu-record-times__row">
                                      <span className="temu-record-times__label">{t('publishRecord.createdLabel')}</span>
                                      <span>{formatShanghaiDateTime(createdAt)}</span>
                                    </div>
                                    <div className="temu-record-times__row">
                                      <span className="temu-record-times__label">{t('publishRecord.publishedLabel')}</span>
                                      <span>{formatShanghaiDateTime(publishedAt)}</span>
                                    </div>
                                    <div className="temu-record-times__row">
                                      <span className="temu-record-times__label">{t('publishRecord.listedLabel')}</span>
                                      <span>
                                        {shelfLabel === '自动上架' || shelfLabel === '—'
                                          ? (shelfLabel === '自动上架' ? t('publishRecord.autoList') : shelfLabel)
                                          : formatShanghaiDateTime(shelfLabel)}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="temu-product-cell temu-draft-cell--actions"
                                >
                                  <div className="temu-draft-actions">
                                    <button
                                      type="button"
                                      className="wf-tasks-actions__link"
                                      disabled={loading || editingId === item.id}
                                      onClick={() => onEdit(item)}
                                    >
                                      {editingId === item.id ? t('publishRecord.checking') : t('publishRecord.edit')}
                                    </button>
                                    <button
                                      type="button"
                                      className="wf-tasks-actions__link"
                                      disabled={loading || copyingId === item.id || editingId === item.id}
                                      onClick={() => onCopyToDraft(item)}
                                    >
                                      {copyingId === item.id ? t('publishRecord.copying') : t('publishRecord.copy')}
                                    </button>
                                  </div>
                                </td>
                              </>
                            ) : null}
                          </tr>
                        );
                      });
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <TemuOpsPager
            page={page}
            pageSize={pageSize}
            total={total}
            loading={loading}
            disabled={!shopId}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            ariaLabel={t('publishRecord.pager')}
          />
        </div>
      </ProductListCard>

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={batchModalTitle}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={batchModalMeta?.placeholder ? t(batchModalMeta.placeholder) : ''}
        onConfirm={onBatchConfirm}
        onClose={() => setBatchModalField(null)}
      />

      <PublishRecordSkuModal
        open={Boolean(skuDetailModal)}
        skc={skuDetailModal?.skc}
        lines={skuDetailModal?.lines || []}
        recordProductSkuIds={skuDetailModal?.recordProductSkuIds || []}
        onClose={() => setSkuDetailModal(null)}
      />
    </>
  );
}
